import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { PiXBold } from "react-icons/pi";
import { putApi } from "../../services/api/globalApi";
import { successMessage, errorMessage, warningMessage } from "../../utils/toastify-message/ToastifyMessage";
import { TYPE_LABEL, apiErrorMessage, formatDuration, publicFileUrl, toFaDigits } from "./mediaUtils";
import { STATUS_META } from "./statusMeta";

const API_PATH = "admin/customer-experiences";

function Preview({ item }) {
  if (item.type === "video" && item.videoSrc) {
    return <video src={publicFileUrl(item.videoSrc)} poster={item.posterSrc ? publicFileUrl(item.posterSrc) : undefined} controls playsInline className="max-h-72 w-full rounded-lg bg-black object-contain" />;
  }
  if (item.type === "voice" && item.voiceSrc) {
    return <audio src={publicFileUrl(item.voiceSrc)} controls className="w-full" />;
  }
  if (item.type === "photo" && item.imageSrc) {
    return <img src={publicFileUrl(item.imageSrc)} alt={item.text} className="max-h-72 w-full rounded-lg bg-gray-100 object-contain" />;
  }
  return null;
}

export default function ReviewModal({ item, onClose, onDone }) {
  const [note, setNote] = useState(item.reviewNote || "");
  const [saving, setSaving] = useState(false);
  const meta = STATUS_META[item.status];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [saving, onClose]);

  const decide = async (status) => {
    if ((status === "needsRevision" || status === "rejected") && !note.trim()) {
      return warningMessage("دلیل را برای مشتری بنویسید");
    }
    setSaving(true);
    try {
      await putApi({
        path: `${API_PATH}/${item._id}`,
        body: { status, reviewNote: status === "published" ? "" : note.trim() },
      });
      successMessage(status === "published" ? "تجربه منتشر شد" : "نتیجه برای مشتری ثبت شد");
      onDone();
    } catch (error) {
      errorMessage(apiErrorMessage(error, "خطا در ثبت نتیجه"));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4" dir="rtl" role="dialog" aria-modal="true" aria-label="بررسی تجربه">
      <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 dark:text-white">بررسی تجربه</h3>
          <button type="button" onClick={onClose} disabled={saving} aria-label="بستن" className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-gray-700">
            <PiXBold size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-white">{item.name}</span>
            {item.user?.mobile && <span dir="ltr" className="text-xs text-gray-400">{item.user.mobile}</span>}
            <span className={`rounded px-2 py-0.5 text-xs ${meta.cls}`}>{meta.label}</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {item.productName} · {TYPE_LABEL[item.type]}
            {item.durationSec > 0 && <> · <span dir="ltr">{formatDuration(item.durationSec)}</span></>}
            {item.type === "text" && <> · {toFaDigits(item.rating)} ستاره</>}
          </div>
          <Preview item={item} />
          <p className="whitespace-pre-line rounded-lg bg-gray-50 p-3 text-sm leading-7 text-gray-800 dark:bg-gray-700 dark:text-gray-100">{item.text}</p>

          <div>
            <label htmlFor="review-note" className="mb-2 block text-sm font-medium text-gray-900 dark:text-white">
              پیام برای مشتری (برای «نیاز به اصلاح» و «رد» الزامی است)
            </label>
            <textarea
              id="review-note"
              rows={3}
              maxLength={300}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="مثال: لطفاً نام برندهای دیگر را از متن حذف کنید."
              className="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" disabled={saving} onClick={() => decide("published")} className="h-10 rounded-lg bg-green-700 px-5 text-sm font-medium text-white hover:bg-green-800 disabled:opacity-60">
            تأیید و انتشار
          </button>
          <button type="button" disabled={saving} onClick={() => decide("needsRevision")} className="h-10 rounded-lg bg-amber-500 px-5 text-sm font-medium text-white hover:bg-amber-600 disabled:opacity-60">
            نیاز به اصلاح
          </button>
          <button type="button" disabled={saving} onClick={() => decide("rejected")} className="h-10 rounded-lg bg-red-700 px-5 text-sm font-medium text-white hover:bg-red-800 disabled:opacity-60">
            رد
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
